'use client';

import React from 'react';
import NavBar from '@/components/header/NavBar';
import Why from '@/components/whySection';
import ContactForm from '@/components/ContactForm/ContactForm';
import Footer from '@/components/Footer/Footer';
import Image from "next/image";
import { FaAngleDoubleRight } from "react-icons/fa";

const features = [
  {
    title: "Detailed Property Listings",
    description:
      "Every listing includes full property details, photos, floor plans, and neighborhood information, giving buyers everything they need to make a confident decision.",
  },
  {
    title: "Live Weather Integration",
    description:
      "Each property page pulls live local weather data, giving buyers a real sense of the climate and conditions around the property they're considering.",
  },
  {
    title: "Visit Scheduling",
    description:
      "Buyers can book a property visit directly from the listing page, choosing a time that works for them without a single back-and-forth phone call.",
  },
  {
    title: "Broker Dashboard",
    description:
      "Brokers get a dedicated dashboard to track every listing's status, manage incoming visit requests, and follow leads from first contact to closed deal.",
  },
  {
    title: "Appointment Status Tracking",
    description:
      "Every scheduled visit moves through a clear status pipeline, so brokers and clients always know what's confirmed, pending, or completed.",
  },
  {
    title: "4-Language Platform",
    description:
      "Listings, appointment booking, and the broker dashboard are fully localized across 4 languages, letting Livside serve buyers and brokers across different markets.",
  },
];

const gallery = [
  {
    src: "/livside/4.avif",
    title: "Property Listings",
    content:
      "Rich, detailed listing pages with photos, key property data, and live local weather, giving buyers a fuller picture before they ever schedule a visit.",
  },
  {
    src: "/livside/5.avif",
    title: "Visit Scheduling",
    content:
      "A simple booking flow lets buyers pick a time to visit a property, instantly notifying the broker and adding it to their dashboard.",
  },
  {
    src: "/livside/6.avif",
    title: "Broker Dashboard",
    content:
      "Brokers track every listing and appointment status in one place, from new inquiry through scheduled visit to closed sale.",
  },
];

const stack = {
  Frontend: ["TypeScript", "Next.js", "Tailwind CSS"],
  Backend: ["Django", "Celery", "PostgreSQL"],
};

const ContactForms: React.FC = () => {
  return (
    <div className="rounded-b-3xl">
      {/* Hero */}
      {/* Hero */}
      <div
        className="bg-cover bg-center bg-no-repeat h-96 lg:h-[600px] mb-2 relative rounded-b-3xl"
        style={{ backgroundImage: `url('/livside/2.avif')` }}
      >
        <div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[600px] rounded-b-3xl" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 gap-4 lg:gap-6">
          <p className="text-white/70 text-sm lg:text-base tracking-wide">
            Real Estate · Broker Platform
          </p>
          <h1 className="text-white text-5xl lg:text-9xl font-extrabold">
            Livside
          </h1>
          <p className="text-white/80 text-lg lg:text-2xl max-w-2xl">
            A full real estate broker platform with live weather data, visit
            scheduling, and a dashboard to manage it all.
          </p>
          <a
            href="https://www.livside.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 lg:mt-4 inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-full text-sm lg:text-base hover:bg-white/90 transition-colors"
          >
            Visit livside.com <FaAngleDoubleRight size={14} />
          </a>
        </div>
      </div>

      <NavBar />

      {/* Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 bg-background">
        <div className="relative w-full h-72 lg:h-auto">
          <Image
            src="/livside/1.avif"
            alt="Livside platform overview"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
          />
        </div>
        <div className="space-y-6 lg:space-y-8 p-6 md:p-12 lg:p-24 flex flex-col justify-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold text-primary">
            Project Overview
          </h2>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            Livside needed a platform that went beyond a standard listings
            site. Buyers wanted real, detailed information before committing
            to a visit, and brokers needed a way to manage that flow of
            interest without losing track of who wanted to see what, and
            when.
          </p>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            We built detailed property pages enriched with live weather data,
            a visit scheduling system connected directly to each listing, and
            a broker dashboard to track every appointment's status from
            request to completed visit, all localized across 4 languages on
            a Next.js and TypeScript frontend, backed by Django and Celery.
          </p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-primary py-12 lg:py-20 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 text-center">
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">4</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Languages supported</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">☀</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Live weather on every listing</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">📅</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Instant visit scheduling</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">◎</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Full broker status dashboard</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-background p-6 md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold text-primary mb-8 lg:mb-16">
          What We Built
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
          {features.map((feature, i) => (
            <div
              key={i}
              className="px-5 py-5 lg:px-6 lg:py-6 border border-dashed border-secondary rounded-2xl"
            >
              <h3 className="text-lg lg:text-xl font-bold mb-2">
                {feature.title}
              </h3>
              <p className="text-sm lg:text-base text-neutral-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-secondary p-6 text-white md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          A Closer Look
        </h2>
        <div className="space-y-12 lg:space-y-20">
          {gallery.map((item, i) => {
            const isReversed = i % 2 === 1;
            return (
              <div
                key={i}
                className={`flex flex-col lg:gap-16 items-center ${
                  isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <div className="relative w-full h-64 sm:h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-lg">
                  <Image
                    src={item.src}
                    alt={item.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover rounded-3xl"
                  />
                </div>
                <div className="w-full space-y-3 lg:space-y-6 mt-6 lg:mt-0">
                  <h4 className="text-2xl lg:text-4xl font-bold">
                    {item.title}
                  </h4>
                  <p className="text-base lg:text-xl leading-relaxed text-neutral-600">
                    {item.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tech stack */}
      <div className="bg-primary p-6 md:p-12 lg:p-24 text-white">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          Tech Stack
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {Object.entries(stack).map(([group, items]) => (
            <div key={group}>
              <p className="text-white/60 text-sm lg:text-base mb-4">{group}</p>
              <div className="flex flex-wrap gap-2 lg:gap-3">
                {items.map((item) => (
                  <span
                    key={item}
                    className="px-4 py-2 rounded-full border border-white/30 text-sm lg:text-base"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default ContactForms;